import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/_core/hooks/useAuth';
import { getLoginUrl } from '@/const';
import { toast } from 'sonner';
import { Loader2, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

type JoinState = 'loading' | 'success' | 'already_connected' | 'error' | 'waiting_auth';

export default function JoinTrainer() {
  const [, navigate] = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [joinState, setJoinState] = useState<JoinState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const hasAttempted = useRef(false);

  // Read invite code from path parameter: /join/:code
  // This avoids iOS Safari iMessage query-string stripping
  const params = useParams<{ code: string }>();
  const code = params.code ?? '';

  const acceptInviteMutation = trpc.trainerPortal.acceptInvite.useMutation({
    onSuccess: () => {
      setJoinState('success');
      toast.success("You're now connected to your trainer. Welcome to The Forge Floor.");
      setTimeout(() => navigate('/'), 2000);
    },
    onError: (err: any) => {
      if (err.message === 'ALREADY_CONNECTED') {
        setJoinState('already_connected');
        toast.info("You're already connected to this trainer.");
        setTimeout(() => navigate('/'), 2500);
      } else {
        setJoinState('error');
        setErrorMessage(err.message || 'This invite link is invalid or has already been used.');
      }
    },
  });

  useEffect(() => {
    // Wait until auth check is done
    if (authLoading) return;

    // No code in path — show error immediately
    if (!code) {
      setJoinState('error');
      setErrorMessage('No invite code found in this link.');
      return;
    }

    // Not logged in — redirect to login, preserving /join/<code> as return path.
    // returnPath is encoded into the OAuth state so it survives the full round-trip
    // and the callback redirects back here after successful authentication.
    if (!isAuthenticated) {
      setJoinState('waiting_auth');
      const returnPath = `/join/${code}`;
      window.location.href = getLoginUrl(returnPath);
      return;
    }

    // Logged in — accept the invite (only once)
    if (!hasAttempted.current) {
      hasAttempted.current = true;
      acceptInviteMutation.mutate({ inviteCode: code });
    }
  }, [authLoading, isAuthenticated, code]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-6 p-6">
      <div className="w-full max-w-sm space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="h-14 w-14 bg-primary flex items-center justify-center rounded-sm rotate-3 mx-auto">
            <Users className="h-8 w-8 text-primary-foreground -rotate-3" />
          </div>
          <h1 className="font-mono text-xl font-bold tracking-wider text-primary">THE FORGE FLOOR</h1>
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">Trainer Invite</p>
        </div>

        {/* State: loading / processing */}
        {(joinState === 'loading' || joinState === 'waiting_auth') && (
          <div className="forge-panel p-6 flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
              {joinState === 'waiting_auth' ? 'Redirecting to sign in…' : 'Processing invite…'}
            </p>
          </div>
        )}

        {/* State: success */}
        {joinState === 'success' && (
          <div className="forge-panel p-6 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p className="font-mono text-sm font-bold text-success uppercase tracking-wider">Connected!</p>
            <p className="text-xs text-muted-foreground">You're now connected to your trainer. Redirecting to The Forge Floor…</p>
          </div>
        )}

        {/* State: already connected */}
        {joinState === 'already_connected' && (
          <div className="forge-panel p-6 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <p className="font-mono text-sm font-bold text-primary uppercase tracking-wider">Already Connected</p>
            <p className="text-xs text-muted-foreground">You're already connected to this trainer. Redirecting home…</p>
          </div>
        )}

        {/* State: error */}
        {joinState === 'error' && (
          <div className="forge-panel p-6 flex flex-col items-center gap-4 text-center">
            <AlertTriangle className="h-10 w-10 text-destructive" />
            <div className="space-y-1">
              <p className="font-mono text-sm font-bold text-destructive uppercase tracking-wider">Invite Invalid</p>
              <p className="text-xs text-muted-foreground">
                {errorMessage || 'This invite link is invalid or has already been used.'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="font-mono text-xs uppercase tracking-wider w-full"
            >
              Go to Command
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
